import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import {
  Calculator,
  CreditCard,
  Banknote,
  Building2,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react';
import { api } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { FINANCIAL_METRICS } from '@creditnova/shared';

export const Route = createFileRoute('/templates/new')({
  component: NewTemplateWizard,
  validateSearch: (search: Record<string, unknown>) => ({
    edit: (search.edit as string) || undefined,
  }),
});

interface TemplateConfig {
  global?: {
    writeupType?: string;
    summaryStyle?: string;
    summaryLength?: string;
    customInstructions?: string;
  };
  modules?: {
    financial?: {
      enabled: boolean;
      statementType?: string;
      selectedMetricIds: string[];
      customRatios: Array<{ name: string; formula: string }>;
      offBalanceSheet: any[];
    };
    creditReport?: {
      enabled: boolean;
      inclusionMode?: string;
      selectedKeys?: any;
    };
    bank?: {
      enabled: boolean;
      type?: string;
    };
    corporate?: {
      enabled: boolean;
      additionalFields?: Record<string, any>;
    };
  };
}

interface FormData {
  name: string;
  description: string;
  writeupType: string;
  isDefault: boolean;
  financialEnabled: boolean;
  financialStatementType: string;
  selectedMetrics: Set<string>;
  customRatios: Array<{ name: string; formula: string }>;
  creditReportEnabled: boolean;
  bankEnabled: boolean;
  bankType: string;
  corporateEnabled: boolean;
  summaryStyle: string;
  summaryLength: string;
  customInstructions: string;
}

const WRITEUP_TYPES = [
  { value: 'interim_fs', label: 'Interim Financial Statements' },
  { value: 'fye_accountant', label: 'FYE Accountant' },
  { value: 'full_analysis', label: 'Full Analysis' },
  { value: 'combined_financial_analysis', label: 'Combined Financial Analysis' },
  { value: 'underwriting_summary', label: 'Underwriting Summary' },
  { value: 'custom_credit_writeup', label: 'Custom Credit Writeup' },
  { value: 'bank_statements', label: 'Bank Statements' },
];

const BANK_TYPES = [
  { value: 'individual', label: 'Individual Account' },
  { value: 'business', label: 'Business Account' },
  { value: 'dynamic_capital', label: 'Dynamic Capital' },
];

const STEPS = [
  { id: 1, title: 'Basics', description: 'Template name and general settings' },
  {
    id: 2,
    title: 'Document Modules',
    description: 'Choose which modules to include',
  },
  {
    id: 3,
    title: 'Metrics & Ratios',
    description: 'Select financial metrics and ratios',
  },
  {
    id: 4,
    title: 'Summary Preferences',
    description: 'Configure output style and instructions',
  },
  { id: 5, title: 'Review & Save', description: 'Confirm and save template' },
];

function NewTemplateWizard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { edit: editId } = Route.useSearch();
  const isEditMode = !!editId;

  const [currentStep, setCurrentStep] = useState(1);
  const [newRatioName, setNewRatioName] = useState('');
  const [newRatioFormula, setNewRatioFormula] = useState('');
  const [initialized, setInitialized] = useState(!isEditMode);

  // Fetch existing template data when in edit mode
  const { data: existingTemplate } = useQuery({
    queryKey: ['templates', editId],
    queryFn: () => api.templates.get(editId!),
    enabled: isEditMode,
  });

  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    writeupType: 'full_analysis',
    isDefault: false,
    financialEnabled: true,
    financialStatementType: 'fye_accountant',
    selectedMetrics: new Set(),
    customRatios: [],
    creditReportEnabled: false,
    bankEnabled: false,
    bankType: 'individual',
    corporateEnabled: false,
    summaryStyle: 'comprehensive',
    summaryLength: 'standard',
    customInstructions: '',
  });

  // Populate form data when editing an existing template
  useEffect(() => {
    if (!isEditMode || !existingTemplate || initialized) return;

    const t = existingTemplate.data || existingTemplate;
    const config = t.config || {};
    const modules = config.modules || {};
    const global = config.global || {};

    setFormData({
      name: t.name || '',
      description: t.description || '',
      writeupType: global.writeupType || 'full_analysis',
      isDefault: t.isDefault || false,
      financialEnabled: modules.financial?.enabled ?? true,
      financialStatementType: modules.financial?.statementType || 'fye_accountant',
      selectedMetrics: new Set(modules.financial?.selectedMetricIds || []),
      customRatios: modules.financial?.customRatios || [],
      creditReportEnabled: modules.creditReport?.enabled ?? false,
      bankEnabled: modules.bank?.enabled ?? false,
      bankType: modules.bank?.type || 'individual',
      corporateEnabled: modules.corporate?.enabled ?? false,
      summaryStyle: global.summaryStyle || 'comprehensive',
      summaryLength: global.summaryLength || 'standard',
      customInstructions: global.customInstructions || '',
    });
    setInitialized(true);
  }, [existingTemplate, isEditMode, initialized]);

  const saveMutation = useMutation({
    mutationFn: async (data: TemplateConfig) => {
      if (isEditMode) {
        return api.templates.update(editId!, {
          name: formData.name,
          description: formData.description || undefined,
          config: data,
          isDefault: formData.isDefault,
        });
      }
      return api.templates.create({
        name: formData.name,
        description: formData.description || undefined,
        config: data,
        isDefault: formData.isDefault,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast({
        title: 'Success',
        description: isEditMode ? 'Template updated successfully' : 'Template created successfully',
        variant: 'default',
      });
      navigate({ to: '/templates' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || `Failed to ${isEditMode ? 'update' : 'create'} template`,
        variant: 'destructive',
      });
    },
  });

  const handleNext = () => {
    if (currentStep === 2 && !formData.financialEnabled) {
      setCurrentStep(4);
    } else if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep === 4 && !formData.financialEnabled) {
      setCurrentStep(2);
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleAddCustomRatio = () => {
    if (newRatioName.trim() && newRatioFormula.trim()) {
      setFormData({
        ...formData,
        customRatios: [
          ...formData.customRatios,
          { name: newRatioName, formula: newRatioFormula },
        ],
      });
      setNewRatioName('');
      setNewRatioFormula('');
    }
  };

  const handleRemoveCustomRatio = (index: number) => {
    setFormData({
      ...formData,
      customRatios: formData.customRatios.filter((_, i) => i !== index),
    });
  };

  const toggleMetric = (metricId: string) => {
    const newSelected = new Set(formData.selectedMetrics);
    if (newSelected.has(metricId)) {
      newSelected.delete(metricId);
    } else {
      newSelected.add(metricId);
    }
    setFormData({ ...formData, selectedMetrics: newSelected });
  };

  const handleCreateTemplate = () => {
    if (!formData.name.trim()) {
      toast({
        title: 'Error',
        description: 'Template name is required',
        variant: 'destructive',
      });
      return;
    }

    const config: TemplateConfig = {
      global: {
        writeupType: formData.writeupType,
        summaryStyle: formData.summaryStyle,
        summaryLength: formData.summaryLength,
        customInstructions:
          formData.customInstructions || undefined,
      },
      modules: {
        financial: {
          enabled: formData.financialEnabled,
          statementType: formData.financialEnabled
            ? formData.financialStatementType
            : undefined,
          selectedMetricIds: Array.from(formData.selectedMetrics),
          customRatios: formData.customRatios,
          offBalanceSheet: [],
        },
        creditReport: {
          enabled: formData.creditReportEnabled,
          inclusionMode: formData.creditReportEnabled ? 'automatic' : undefined,
          selectedKeys: {},
        },
        bank: {
          enabled: formData.bankEnabled,
          type: formData.bankEnabled ? formData.bankType : undefined,
        },
        corporate: {
          enabled: formData.corporateEnabled,
          additionalFields: {},
        },
      },
    };

    saveMutation.mutate(config);
  };

  const groupedMetrics = FINANCIAL_METRICS.reduce(
    (acc: Record<string, Array<(typeof FINANCIAL_METRICS)[number]>>, metric) => {
      if (!acc[metric.category]) {
        acc[metric.category] = [];
      }
      acc[metric.category].push(metric);
      return acc;
    },
    {} as Record<string, Array<(typeof FINANCIAL_METRICS)[number]>>
  );

  // Show loading state while fetching template data in edit mode
  if (isEditMode && !initialized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading template...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            {isEditMode ? 'Edit Analysis Template' : 'Create Analysis Template'}
          </h1>
          <p className="text-muted-foreground">
            {isEditMode
              ? 'Modify your template configuration'
              : 'Build a reusable template for analyzing financial documents'}
          </p>
        </div>

        <div className="mb-8 flex justify-between">
          {STEPS.map((step) => (
            <div
              key={step.id}
              className="flex flex-col items-center flex-1"
            >
              <div
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center font-semibold mb-2 transition-all',
                  currentStep >= step.id
                    ? 'bg-gold-500 text-slate-950'
                    : 'bg-slate-700 text-muted-foreground'
                )}
              >
                {step.id}
              </div>
              <div className="text-center">
                <p
                  className={cn(
                    'text-sm font-medium',
                    currentStep === step.id
                      ? 'text-gold-400'
                      : currentStep > step.id
                        ? 'text-gold-500'
                        : 'text-muted-foreground'
                  )}
                >
                  {step.title}
                </p>
              </div>
              {step.id < STEPS.length && (
                <div
                  className={cn(
                    'h-1 flex-1 mt-2 mx-2',
                    currentStep > step.id
                      ? 'bg-gold-500'
                      : 'bg-slate-700'
                  )}
                />
              )}
            </div>
          ))}
        </div>

        <div className="glass glass-elevated rounded-xl p-8 min-h-96">
          {currentStep === 1 && <StepBasics formData={formData} setFormData={setFormData} />}
          {currentStep === 2 && (
            <StepDocumentModules formData={formData} setFormData={setFormData} />
          )}
          {currentStep === 3 && formData.financialEnabled && (
            <StepMetricsAndRatios
              formData={formData}
              groupedMetrics={groupedMetrics}
              newRatioName={newRatioName}
              setNewRatioName={setNewRatioName}
              newRatioFormula={newRatioFormula}
              setNewRatioFormula={setNewRatioFormula}
              onAddCustomRatio={handleAddCustomRatio}
              onRemoveCustomRatio={handleRemoveCustomRatio}
              toggleMetric={toggleMetric}
            />
          )}
          {currentStep === 4 && (
            <StepSummaryPreferences formData={formData} setFormData={setFormData} />
          )}
          {currentStep === 5 && (
            <StepReviewAndSave
              formData={formData}
              groupedMetrics={groupedMetrics}
            />
          )}
        </div>

        <div className="mt-8 flex justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="border-slate-600 text-white hover:bg-slate-800"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {currentStep < 5 ? (
            <Button
              onClick={handleNext}
              className="bg-gold-500 hover:bg-gold-600 text-slate-950 font-semibold"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleCreateTemplate}
              disabled={saveMutation.isPending}
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
            >
              {saveMutation.isPending
                ? (isEditMode ? 'Saving...' : 'Creating...')
                : (isEditMode ? 'Save Changes' : 'Create Template')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepBasics({
  formData,
  setFormData,
}: {
  formData: FormData;
  setFormData: (data: FormData) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white mb-6">Template Basics</h2>
        <p className="text-muted-foreground mb-6">
          Start by providing a name and selecting your preferred output format.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Template Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) =>
              setFormData({ ...formData, name: e.target.value })
            }
            placeholder="e.g., Quick Credit Analysis, Detailed FYE Review"
            className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-muted-foreground focus:border-gold-500 focus:outline-none transition-colors"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Description (Optional)
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            placeholder="Describe the purpose of this template..."
            rows={3}
            className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-muted-foreground focus:border-gold-500 focus:outline-none transition-colors resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Writeup Type
          </label>
          <select
            value={formData.writeupType}
            onChange={(e) =>
              setFormData({ ...formData, writeupType: e.target.value })
            }
            className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-gold-500 focus:outline-none transition-colors"
          >
            {WRITEUP_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3 pt-4">
          <input
            type="checkbox"
            id="isDefault"
            checked={formData.isDefault}
            onChange={(e) =>
              setFormData({ ...formData, isDefault: e.target.checked })
            }
            className="w-4 h-4 bg-slate-800 border border-slate-700 rounded cursor-pointer accent-gold-500"
          />
          <label htmlFor="isDefault" className="text-sm text-white cursor-pointer">
            Set as default template
          </label>
        </div>
      </div>
    </div>
  );
}

function StepDocumentModules({
  formData,
  setFormData,
}: {
  formData: FormData;
  setFormData: (data: FormData) => void;
}) {
  const modules = [
    {
      id: 'financial',
      icon: Calculator,
      label: 'Financial Statements',
      description: 'Analyze income statements, balance sheets, and cash flows',
      enabled: formData.financialEnabled,
    },
    {
      id: 'creditReport',
      icon: CreditCard,
      label: 'Credit Reports',
      description: 'Include credit report analysis and scoring',
      enabled: formData.creditReportEnabled,
    },
    {
      id: 'bank',
      icon: Banknote,
      label: 'Bank Statements',
      description: 'Analyze bank account activity and trends',
      enabled: formData.bankEnabled,
    },
    {
      id: 'corporate',
      icon: Building2,
      label: 'Corporate Search',
      description: 'Include corporate registration and filing information',
      enabled: formData.corporateEnabled,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white mb-6">Document Modules</h2>
        <p className="text-muted-foreground mb-6">
          Select which document types this template will analyze.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {modules.map((module) => {
          const Icon = module.icon;
          return (
            <div
              key={module.id}
              onClick={() => {
                if (module.id === 'financial') {
                  setFormData({
                    ...formData,
                    financialEnabled: !formData.financialEnabled,
                  });
                } else if (module.id === 'creditReport') {
                  setFormData({
                    ...formData,
                    creditReportEnabled: !formData.creditReportEnabled,
                  });
                } else if (module.id === 'bank') {
                  setFormData({
                    ...formData,
                    bankEnabled: !formData.bankEnabled,
                  });
                } else if (module.id === 'corporate') {
                  setFormData({
                    ...formData,
                    corporateEnabled: !formData.corporateEnabled,
                  });
                }
              }}
              className={cn(
                'p-4 rounded-lg border-2 cursor-pointer transition-all',
                module.enabled
                  ? 'border-gold-500 bg-slate-800/50'
                  : 'border-slate-700 bg-slate-800/20 hover:border-slate-600'
              )}
            >
              <div className="flex items-start gap-3">
                <div
                  className={cn(
                    'p-2 rounded-lg',
                    module.enabled
                      ? 'bg-gold-500/20 text-gold-400'
                      : 'bg-slate-700 text-muted-foreground'
                  )}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3
                    className={cn(
                      'font-semibold',
                      module.enabled ? 'text-gold-400' : 'text-white'
                    )}
                  >
                    {module.label}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {module.description}
                  </p>
                </div>
                <div
                  className={cn(
                    'w-5 h-5 rounded border-2 mt-1 flex items-center justify-center transition-all',
                    module.enabled
                      ? 'border-gold-500 bg-gold-500'
                      : 'border-slate-600'
                  )}
                >
                  {module.enabled && (
                    <svg
                      className="w-3 h-3 text-slate-950 font-bold"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {formData.financialEnabled && (
        <div className="border-t border-slate-700 pt-6 mt-6">
          <label className="block text-sm font-medium text-white mb-3">
            Financial Statement Type
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { value: 'interim_fs', label: 'Interim Financial Statements' },
              { value: 'fye_accountant', label: 'FYE Accountant' },
            ].map((type) => (
              <button
                key={type.value}
                onClick={() =>
                  setFormData({
                    ...formData,
                    financialStatementType: type.value,
                  })
                }
                className={cn(
                  'px-4 py-2 rounded-lg border-2 transition-all font-medium',
                  formData.financialStatementType === type.value
                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                    : 'border-slate-700 bg-slate-800/20 text-muted-foreground hover:border-slate-600'
                )}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {formData.bankEnabled && (
        <div className="border-t border-slate-700 pt-6 mt-6">
          <label className="block text-sm font-medium text-white mb-3">
            Bank Account Type
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {BANK_TYPES.map((type) => (
              <button
                key={type.value}
                onClick={() =>
                  setFormData({ ...formData, bankType: type.value })
                }
                className={cn(
                  'px-4 py-2 rounded-lg border-2 transition-all font-medium',
                  formData.bankType === type.value
                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                    : 'border-slate-700 bg-slate-800/20 text-muted-foreground hover:border-slate-600'
                )}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StepMetricsAndRatios({
  formData,
  groupedMetrics,
  newRatioName,
  setNewRatioName,
  newRatioFormula,
  setNewRatioFormula,
  onAddCustomRatio,
  onRemoveCustomRatio,
  toggleMetric,
}: {
  formData: FormData;
  groupedMetrics: Record<string, Array<(typeof FINANCIAL_METRICS)[number]>>;
  newRatioName: string;
  setNewRatioName: (value: string) => void;
  newRatioFormula: string;
  setNewRatioFormula: (value: string) => void;
  onAddCustomRatio: () => void;
  onRemoveCustomRatio: (index: number) => void;
  toggleMetric: (metricId: string) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white mb-2">
          Financial Metrics & Ratios
        </h2>
        <p className="text-muted-foreground mb-6">
          Select which financial metrics to include in your analysis.
        </p>
      </div>

      <div className="space-y-6">
        {Object.entries(groupedMetrics).map(([category, metrics]) => (
          <div key={category} className="space-y-3">
            <h3 className="text-sm font-semibold text-gold-400 uppercase tracking-wide">
              {category}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {metrics.map((metric) => (
                <div
                  key={metric.id}
                  onClick={() => toggleMetric(metric.id)}
                  className={cn(
                    'p-3 rounded-lg border-2 cursor-pointer transition-all',
                    formData.selectedMetrics.has(metric.id)
                      ? 'border-gold-500 bg-slate-800/50'
                      : 'border-slate-700 bg-slate-800/20 hover:border-slate-600'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'w-4 h-4 rounded border-2 flex items-center justify-center transition-all',
                        formData.selectedMetrics.has(metric.id)
                          ? 'border-gold-500 bg-gold-500'
                          : 'border-slate-600'
                      )}
                    >
                      {formData.selectedMetrics.has(metric.id) && (
                        <svg
                          className="w-2.5 h-2.5 text-slate-950 font-bold"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">
                        {metric.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {metric.formula}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-slate-700 pt-6 mt-6">
        <h3 className="text-sm font-semibold text-gold-400 uppercase tracking-wide mb-4">
          Custom Ratios
        </h3>

        {formData.customRatios.length > 0 && (
          <div className="space-y-2 mb-4">
            {formData.customRatios.map((ratio, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg border border-slate-700"
              >
                <div>
                  <p className="text-sm font-medium text-white">{ratio.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">
                    {ratio.formula}
                  </p>
                </div>
                <button
                  onClick={() => onRemoveCustomRatio(index)}
                  className="text-muted-foreground hover:text-red-400 transition-colors"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-3 bg-slate-800/20 p-4 rounded-lg border border-slate-700">
          <div>
            <label className="block text-xs font-medium text-white mb-2">
              Ratio Name
            </label>
            <input
              type="text"
              value={newRatioName}
              onChange={(e) => setNewRatioName(e.target.value)}
              placeholder="e.g., Debt to Equity Ratio"
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-white placeholder-muted-foreground focus:border-gold-500 focus:outline-none text-sm transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-white mb-2">
              Formula
            </label>
            <input
              type="text"
              value={newRatioFormula}
              onChange={(e) => setNewRatioFormula(e.target.value)}
              placeholder="e.g., Total Debt / Total Equity"
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-white placeholder-muted-foreground focus:border-gold-500 focus:outline-none text-sm transition-colors"
            />
          </div>
          <button
            onClick={onAddCustomRatio}
            disabled={!newRatioName.trim() || !newRatioFormula.trim()}
            className="w-full px-3 py-2 bg-gold-500/20 text-gold-400 border border-gold-500/50 rounded font-medium text-sm hover:bg-gold-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Add Custom Ratio
          </button>
        </div>
      </div>
    </div>
  );
}

function StepSummaryPreferences({
  formData,
  setFormData,
}: {
  formData: FormData;
  setFormData: (data: FormData) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white mb-2">Summary Preferences</h2>
        <p className="text-muted-foreground mb-6">
          Configure how the output is formatted and presented.
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-white mb-4">
            Summary Style
          </label>
          <div className="space-y-2">
            {[
              {
                value: 'comprehensive',
                label: 'Comprehensive',
                description: 'Full narrative with detailed analysis and context',
              },
              {
                value: 'concise',
                label: 'Concise',
                description: 'Brief overview focusing on key findings',
              },
              {
                value: 'bullet-points',
                label: 'Bullet Points',
                description: 'Structured bullet-point format for quick scanning',
              },
              {
                value: 'executive',
                label: 'Executive',
                description: 'High-level executive summary',
              },
            ].map((style) => (
              <div
                key={style.value}
                onClick={() =>
                  setFormData({ ...formData, summaryStyle: style.value })
                }
                className={cn(
                  'p-3 rounded-lg border-2 cursor-pointer transition-all',
                  formData.summaryStyle === style.value
                    ? 'border-gold-500 bg-slate-800/50'
                    : 'border-slate-700 bg-slate-800/20 hover:border-slate-600'
                )}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all',
                      formData.summaryStyle === style.value
                        ? 'border-gold-500 bg-gold-500'
                        : 'border-slate-600'
                    )}
                  >
                    {formData.summaryStyle === style.value && (
                      <div className="w-1.5 h-1.5 bg-slate-950 rounded-full" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">
                      {style.label}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {style.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-slate-700 pt-6">
          <label className="block text-sm font-semibold text-white mb-4">
            Summary Length
          </label>
          <div className="space-y-2">
            {[
              { value: 'brief', label: 'Brief', description: '~150 words' },
              {
                value: 'standard',
                label: 'Standard',
                description: '~300 words',
              },
              {
                value: 'detailed',
                label: 'Detailed',
                description: '~500 words',
              },
              {
                value: 'extensive',
                label: 'Extensive',
                description: '~800+ words',
              },
            ].map((length) => (
              <div
                key={length.value}
                onClick={() =>
                  setFormData({ ...formData, summaryLength: length.value })
                }
                className={cn(
                  'p-3 rounded-lg border-2 cursor-pointer transition-all',
                  formData.summaryLength === length.value
                    ? 'border-gold-500 bg-slate-800/50'
                    : 'border-slate-700 bg-slate-800/20 hover:border-slate-600'
                )}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all',
                      formData.summaryLength === length.value
                        ? 'border-gold-500 bg-gold-500'
                        : 'border-slate-600'
                    )}
                  >
                    {formData.summaryLength === length.value && (
                      <div className="w-1.5 h-1.5 bg-slate-950 rounded-full" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">
                      {length.label}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {length.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-slate-700 pt-6">
          <label className="block text-sm font-semibold text-white mb-2">
            Custom Instructions
          </label>
          <p className="text-xs text-muted-foreground mb-3">
            Add any specific requirements or focus areas for the analysis.
          </p>
          <textarea
            value={formData.customInstructions}
            onChange={(e) =>
              setFormData({
                ...formData,
                customInstructions: e.target.value,
              })
            }
            placeholder="e.g., Focus on working capital trends, ignore non-recurring items..."
            rows={4}
            className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-muted-foreground focus:border-gold-500 focus:outline-none transition-colors resize-none"
          />
        </div>
      </div>
    </div>
  );
}

function StepReviewAndSave({
  formData,
  groupedMetrics,
}: {
  formData: FormData;
  groupedMetrics: Record<string, Array<(typeof FINANCIAL_METRICS)[number]>>;
}) {
  const selectedMetricNames = Array.from(formData.selectedMetrics)
    .map((id) => {
      for (const metrics of Object.values(groupedMetrics)) {
        const metric = metrics.find((m) => m.id === id);
        if (metric) return metric.name;
      }
      return null;
    })
    .filter(Boolean);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white mb-2">Review & Save</h2>
        <p className="text-muted-foreground mb-6">
          Review your template configuration before creating.
        </p>
      </div>

      <div className="space-y-4">
        <div className="bg-slate-800/30 rounded-lg border border-slate-700 p-4">
          <h3 className="text-sm font-semibold text-gold-400 mb-3">
            Template Information
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between items-start">
              <span className="text-sm text-muted-foreground">Name:</span>
              <span className="text-sm font-medium text-white">
                {formData.name || '(Not set)'}
              </span>
            </div>
            {formData.description && (
              <div className="flex justify-between items-start">
                <span className="text-sm text-muted-foreground">Description:</span>
                <span className="text-sm text-white text-right max-w-xs">
                  {formData.description}
                </span>
              </div>
            )}
            <div className="flex justify-between items-start">
              <span className="text-sm text-muted-foreground">Writeup Type:</span>
              <span className="text-sm font-medium text-white">
                {WRITEUP_TYPES.find((t) => t.value === formData.writeupType)
                  ?.label || formData.writeupType}
              </span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-sm text-muted-foreground">Default:</span>
              <span className="text-sm font-medium text-white">
                {formData.isDefault ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/30 rounded-lg border border-slate-700 p-4">
          <h3 className="text-sm font-semibold text-gold-400 mb-3">
            Enabled Modules
          </h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'w-3 h-3 rounded-full',
                  formData.financialEnabled
                    ? 'bg-emerald-500'
                    : 'bg-slate-600'
                )}
              />
              <span className="text-sm text-white">
                Financial Statements
                {formData.financialEnabled &&
                  ` (${
                    formData.financialStatementType === 'interim_fs'
                      ? 'Interim FS'
                      : 'FYE Accountant'
                  })`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'w-3 h-3 rounded-full',
                  formData.creditReportEnabled
                    ? 'bg-emerald-500'
                    : 'bg-slate-600'
                )}
              />
              <span className="text-sm text-white">Credit Reports</span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'w-3 h-3 rounded-full',
                  formData.bankEnabled ? 'bg-emerald-500' : 'bg-slate-600'
                )}
              />
              <span className="text-sm text-white">
                Bank Statements
                {formData.bankEnabled && ` (${formData.bankType})`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'w-3 h-3 rounded-full',
                  formData.corporateEnabled
                    ? 'bg-emerald-500'
                    : 'bg-slate-600'
                )}
              />
              <span className="text-sm text-white">Corporate Search</span>
            </div>
          </div>
        </div>

        {formData.financialEnabled && (
          <div className="bg-slate-800/30 rounded-lg border border-slate-700 p-4">
            <h3 className="text-sm font-semibold text-gold-400 mb-3">
              Selected Metrics
            </h3>
            <div className="space-y-2">
              {selectedMetricNames.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedMetricNames.map((name) => (
                    <span
                      key={name}
                      className="inline-block px-2 py-1 text-xs font-medium bg-gold-500/20 text-gold-400 rounded"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No metrics selected</p>
              )}
            </div>
            {formData.customRatios.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-600">
                <p className="text-xs font-medium text-emerald-400 mb-2">
                  Custom Ratios
                </p>
                <div className="space-y-1">
                  {formData.customRatios.map((ratio, index) => (
                    <div key={index} className="text-xs text-muted-foreground">
                      <span className="font-medium text-white">
                        {ratio.name}
                      </span>
                      : {ratio.formula}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="bg-slate-800/30 rounded-lg border border-slate-700 p-4">
          <h3 className="text-sm font-semibold text-gold-400 mb-3">
            Output Configuration
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between items-start">
              <span className="text-sm text-muted-foreground">Style:</span>
              <span className="text-sm font-medium text-white capitalize">
                {formData.summaryStyle.replace('_', ' ')}
              </span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-sm text-muted-foreground">Length:</span>
              <span className="text-sm font-medium text-white capitalize">
                {formData.summaryLength}
              </span>
            </div>
            {formData.customInstructions && (
              <div className="flex justify-between items-start gap-2 pt-2 border-t border-slate-600">
                <span className="text-sm text-muted-foreground">Instructions:</span>
                <span className="text-sm text-white text-right max-w-xs">
                  {formData.customInstructions}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <p className="text-sm text-muted-foreground italic">
        Click the "Create Template" button below to save this template. You can
        use it for all future document analyses.
      </p>
    </div>
  );
}
